import { type TiledMap } from '@veriverse/map-format';
import { type ActiveMapResponse, type MetaResponse } from '@veriverse/shared';
import { type Room } from 'colyseus.js';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createGame } from '../game/create-game.js';
import { api } from '../net/api.js';
import { useSessionStore } from '../state/session-store.js';
import { useSettingsStore } from '../state/settings.js';

/**
 * Hosts the Phaser canvas for the lifetime of a room connection. The map comes from the server
 * (`GET /api/map`); when the server announces a new one (`map:changed`) the canvas fetches it and
 * swaps games, keeping the old picture on screen until the new map has arrived.
 */
export function GameCanvas({ room }: { room: Room }) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRevision = useSessionStore((s) => s.mapRevision);
  // Animations are built when the game starts, so a new motion setting restarts the game.
  const reduceMotion = useSettingsStore((s) => s.reduceMotion);
  const [map, setMap] = useState<{ map: ActiveMapResponse; meta: MetaResponse }>();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    Promise.all([api<ActiveMapResponse>('/api/map'), api<MetaResponse>('/api/meta')])
      .then(([response, meta]) => {
        if (!cancelled) setMap({ map: response, meta });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [room, mapRevision]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !map) return;
    const game = createGame(container, room, map.map.map as TiledMap, map.meta.office);
    return () => game.destroy(true);
  }, [room, map, reduceMotion]);

  return (
    <div ref={containerRef} className="game-canvas" aria-label="Office world">
      {failed && !map && <p className="notice">{t('map.loadFailed')}</p>}
    </div>
  );
}
